/**
 * The status role model (CUR-1337).
 *
 * A status map never names a colour. It names a ROLE — what the state means in a workflow — and the
 * role resolves to a TONE, which owns every colour field of a `StatusConfig` (the Ant Design tag hue
 * for the outlined variant, the CSS colour for the dot variant, the text and edge colours of the
 * filled pill, and the semantic surface triplet for tinted chips). The same meaning therefore renders
 * the same way in every module and every variant,
 * and re-colouring a meaning is a one-line change to `STATUS_ROLE_TONES`.
 *
 * Choosing a role:
 *
 * | Role        | Means                                                        | Tone    |
 * | ----------- | ------------------------------------------------------------ | ------- |
 * | `open`      | live and healthy: open, active, published, on track          | success |
 * | `attention` | a person has to act: re-opened, due soon, awaiting review    | warning |
 * | `pending`   | work is in flight: in progress, processing, sent, onboarding | info    |
 * | `breached`  | a deadline or target was missed: overdue, off target         | danger  |
 * | `closed`    | the lifecycle ended normally: closed, done, archived         | neutral |
 * | `cancelled` | the lifecycle ended without an outcome: cancelled, skipped   | neutral |
 * | `idle`      | nothing has happened yet: draft, not started, queued         | neutral |
 * | `succeeded` | a process or check returned a good result: passed, completed | success |
 * | `failed`    | a bad or refused result: failed, error, declined, revoked    | danger  |
 * | `queried`   | halted on a question after a verdict step (file audits only) | violet  |
 *
 * `closed` versus `succeeded`: an entity with an open/closed lifecycle (complaint, incident, task) ends
 * `closed` and is neutral, because closing says nothing about the result. A run with a pass/fail result
 * (audit, extraction, delivery) ends `succeeded` or `failed`. Blue never means "finished".
 */
import type { StatusConfig } from './status-config';

type StatusToneConfig = Pick<
    StatusConfig,
    'color' | 'cssColor' | 'bgClass' | 'textClass' | 'borderClass' | 'edgeClass'
>;

/**
 * `color` is the Ant Design tag hue whose outlined rendering is contrast-corrected for both themes in
 * `antd-overrides.css`. `cssColor` drives the dot variant (3:1 target): the four status tokens adapt
 * to the theme, while `--color-status-neutral` and `--color-violet-600` are fixed values. `edgeClass`
 * is the border of the filled pill: the solid role colour (the `-foreground` step for warning), which
 * `check:contrast` holds at 3:1 on every surface a pill is drawn on, in both themes. Violet has none
 * because its solid fill already holds its shape.
 */
export const STATUS_TONES = {
    success: {
        color: 'green',
        cssColor: 'var(--color-success)',
        bgClass: 'bg-success-subtle',
        textClass: 'text-success-foreground',
        borderClass: 'border-success-border',
        edgeClass: 'border-success',
    },
    warning: {
        color: 'orange',
        cssColor: 'var(--color-warning)',
        bgClass: 'bg-warning-subtle',
        textClass: 'text-warning-foreground',
        borderClass: 'border-warning-border',
        // Solid amber is 3.19:1 on the card but 2.89:1 on the canvas and `bg-muted`, so the warning
        // edge takes the `-foreground` step (5.02:1 on the card).
        edgeClass: 'border-warning-foreground',
    },
    danger: {
        color: 'red',
        cssColor: 'var(--color-danger)',
        bgClass: 'bg-danger-subtle',
        textClass: 'text-danger-foreground',
        borderClass: 'border-danger-border',
        edgeClass: 'border-danger',
    },
    info: {
        color: 'blue',
        cssColor: 'var(--color-info)',
        bgClass: 'bg-info-subtle',
        textClass: 'text-info-foreground',
        borderClass: 'border-info-border',
        edgeClass: 'border-info',
    },
    neutral: {
        color: 'default',
        cssColor: 'var(--color-status-neutral)',
        bgClass: 'bg-muted',
        textClass: 'text-muted-foreground',
        borderClass: 'border-separator',
        edgeClass: 'border-subtle-foreground',
    },
    // A fixed ramp, not a semantic surface: white on violet-600 clears 4.5:1 in both themes.
    violet: {
        color: 'purple',
        cssColor: 'var(--color-violet-600)',
        bgClass: 'bg-violet-600',
        textClass: 'text-white',
    },
} as const satisfies Record<string, StatusToneConfig>;

export type StatusTone = keyof typeof STATUS_TONES;

export const STATUS_ROLE_TONES = {
    open: 'success',
    attention: 'warning',
    pending: 'info',
    breached: 'danger',
    closed: 'neutral',
    cancelled: 'neutral',
    idle: 'neutral',
    succeeded: 'success',
    failed: 'danger',
    queried: 'violet',
} as const satisfies Record<string, StatusTone>;

export type StatusRole = keyof typeof STATUS_ROLE_TONES;

/** Build one `StatusConfig` entry from its role and label. Colours come from the role alone. */
export function statusEntry(role: StatusRole, label: string, icon?: StatusConfig['icon']): StatusConfig {
    return { role, label, ...STATUS_TONES[STATUS_ROLE_TONES[role]], ...(icon ? { icon } : {}) };
}

/** Ant Design tag hue for a role, for the few tags that cannot render through `StatusBadge`. */
export function statusRoleTagColor(role: StatusRole): string {
    return STATUS_TONES[STATUS_ROLE_TONES[role]].color;
}

/** Tailwind text colour for a role, for an icon or text that sits beside such a tag. */
export function statusRoleTextClass(role: StatusRole): string {
    return STATUS_TONES[STATUS_ROLE_TONES[role]].textClass;
}
